import { JourneyFeatureFlags } from '@epilot/journey-logic-commons'
import type {
  AvailabilityBlockValue,
  OrganizationId
} from '@epilot/journey-logic-commons/src/types/next'
import { useQuery } from '@tanstack/react-query'

import { getPricingClient } from '../services/pricing-service'
import { selectJourneyOrgId } from '../store/selectors/config'

import { useAppStoreSelector } from './useAppStoreSelector'
import { useFlag } from './useFlags'

type Props = Pick<QueryArgs, 'productIds' | 'filters'>

type QueryArgs = {
  /** Although optional, throws an error if not provided for ease of use */
  organizationId: OrganizationId | undefined
  /** Which Product Ids you want to check availability against */
  productIds: string[]
  /** Location filters */
  filters?: AvailabilityBlockValue
}

export const useProductAvailability = (props: Props) => {
  const organizationId = useAppStoreSelector(selectJourneyOrgId)
  const newEngineEnabled = useFlag(JourneyFeatureFlags.LOGICS_ENGINE)
  const queryArgs: QueryArgs = { organizationId, ...props }

  return useQuery({
    queryKey: productAvailabilityQueryKey(queryArgs),
    queryFn: async ({ signal }) => {
      return getProductAvailability(queryArgs, signal)
    },
    enabled:
      newEngineEnabled && !!queryArgs.organizationId && !!queryArgs.filters
  })
}

export const productAvailabilityQueryKey = (args: QueryArgs) => {
  return ['productAvailability', args]
}

export const getProductAvailability = async (
  { organizationId, productIds, filters }: QueryArgs,
  signal?: AbortSignal
) => {
  if (!organizationId) {
    throw new Error('Expected organizationId to be set')
  }

  const response = await getPricingClient().$availabilityCheck(
    {
      ['X-Ivy-Org-ID']: organizationId
    },
    {
      products: productIds,
      filters: {
        location: {
          postal_code: filters?.zipCode || '%',
          city: filters?.city || '%',
          street: filters?.streetName || '%',
          street_number: filters?.streetNumber || '%'
        }
      }
    },
    { signal }
  )

  if (response.status !== 200) {
    // eslint-disable-next-line no-console
    console.error(response)
    throw new Error('Error fetching availability data')
  }

  return response.data.available_products
}
